import React, { useState, useEffect, useRef } from 'react';
import { Button, TextField, Typography, makeStyles } from '@material-ui/core';
import { useLongPress } from 'use-long-press';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import ToastStr from '../../../request/toastStr';
import { COLORS } from '../../../shared/Theme';
import { SIcon } from '../../../shared/Icon';
import { member as v_member, oper as v_oper, basic, Values } from '../../../request/values';
import i18n, { setSavedLanguage } from '../../../locales';

const SERVER = basic.server;

const useStyles = makeStyles((theme) => ({
	paper: {
		// marginTop: theme.spacing(8),
		paddingTop: '13vh',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		[theme.breakpoints.up('md')]: {
			paddingTop: '0',
		},
	},
	title: {
		marginTop: '5vh',
		width: '100%',
		alignItems: 'left',
		'& img': {
			width: '45vw',
		},
		[theme.breakpoints.up('md')]: {
			width: '80%',
			'& img': {
				width: '80%',
			},
		},
	},
	avatar: {
		margin: theme.spacing(1),
		backgroundColor: theme.palette.secondary.main,
	},
	form: {
		width: '100%', // Fix IE 11 issue.
		marginTop: theme.spacing(1),
		[theme.breakpoints.up('md')]: {
			width: '80%',
		},
	},
	submit: {
		height: 48,
		margin: theme.spacing(3, 0, 2),
		borderRadius: 10,
		border: 'none',
		boxShadow: 'none',
		fontWeight: 700,
		fontFamily: 'NanumB',
		fontSize: '1rem',
		textTransform: 'none',
		backgroundColor: COLORS.WATER_BLUE,
		color: COLORS.WHITE,
		'&:hover': {
			backgroundColor: '#137ab4',
		},
	},
	logoSection: {
		position: 'fixed',
		left: 0,
		right: 0,
		bottom: '5vh', // 바닥에서 5vh 위
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1, // 필요 시 다른 콘텐츠 위에 보이도록
		[theme.breakpoints.up('md')]: {
			bottom: '5vh', // 데스크톱에서도 동일하게 유지
		},
	},
	input: {
		...theme.typography.subtitle1,
	},
	inputLabel: {
		...theme.typography.body1,
		color: '#000',
	},
	desc: {
		...theme.typography.h1,
		[theme.breakpoints.up('md')]: {
			...theme.typography.h5,
		},
	},
	iconButtonPadding: {
		paddingLeft: 0,
		[theme.breakpoints.up('md')]: {
			paddingLeft: '43px',
		},
	},
	logobutton: {
		border: 'none',
		backgroundColor: 'transparent',
	},
}));

const ClassEnter: React.FC<any> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation('student');
	const creatingRef = useRef(false); // prevent duplicate createMember calls
	const [classCode, setClassCode] = useState<string>('');
	const [classVersion, setClassVersion] = useState<Number>(1);
	const params = new URLSearchParams(window.location.search);
	const codeFromUrl = params.get('classCode');
	let history = useHistory();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { member, setMember } = props;
	const [label, setLabel] = useState(t('ClassEnter.labelDefault'));

	const valueChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { id, value } = e.target;
		setClassCode(value);
	};

	const handleFocus = () => {
		setLabel(t('ClassEnter.labelClassCode'));
	};

	const handleBlur = () => {
		if (classCode === '') {
			setLabel(t('ClassEnter.labelDefault'));
		}
	};

	const moveOperator = () => {
		window.location.href = 'https://educator.youstarter.co.kr';
	};

	const buttonToOper = useLongPress(moveOperator, {
		threshold: 2500,
		captureEvent: true,
		cancelOnMovement: false,
	});

	const loginCheck = async (classCodeParam: string) => {
		if (!classCodeParam) {
			enqueueSnackbar(ToastStr.CLASS_CODE_CHECK_STR, { variant: 'warning' });
			return;
		} else if (classCodeParam.length > 12) {
			enqueueSnackbar(ToastStr.CLASS_CODE_LENGTH_CHECK_STR, { variant: 'warning' });
			return;
		}
		const validateResult = await getClassCodeValidate(classCodeParam);
		if (validateResult === 1) {
			enqueueSnackbar(ToastStr.CLASS_CODE_NOT_FOUND_STR, { variant: 'warning' });
			return;
		} else if (validateResult === 2) {
			enqueueSnackbar(ToastStr.TIME_OUT_STR, { variant: 'warning' });
			return;
		}

		setClassCode(classCodeParam); // No longer needed, effect sets it

		let effectiveMember: any | null = null;

		if (member && member.id) {
			effectiveMember = await validateMember(classCodeParam);
			if (effectiveMember === null) {
				effectiveMember = await createMember(classCodeParam);
			}
		} else {
			effectiveMember = await createMember(classCodeParam);
			console.log('effectiveMember', effectiveMember);
		}

		if (!effectiveMember) return;

		if (!effectiveMember.name) {
			console.log('move to NameInput');
			history.push({ pathname: '/NameInput' });
		} else if (!effectiveMember.companyNum || effectiveMember.companyNum === 0 || effectiveMember.companyNum === null) {
			history.push({ pathname: '/CompanyProcess' });
		} else if (effectiveMember.companyNum === -1) {
			history.push({ pathname: '/CompanyAppStandBy' });
		} else {
			history.push({ pathname: '/CompanyInfo' });
		}
	};

	const getClassCodeValidate = async (classCodeParam: string) => {
		try {
			const response = await axios.get(SERVER + v_member.member + v_member.class_validate, {
				params: { classCode: classCodeParam },
				withCredentials: true,
			});
			const data = response.data;
			if (data.code === Values.SUCCESS_CODE) {
				return 0;
			} else {
				return 1;
			}
		} catch (error) {
			console.error('getClassCodeValidate error', error);
			return 2;
		}
	};

	const createMember = async (classCodeParam: string) => {
		if (creatingRef.current) {
			console.log('createMember skipped: already in-flight');
			return null;
		}
		creatingRef.current = true;

		try {
			const response = await axios({
				method: 'POST',
				data: { classCode: classCodeParam },
				withCredentials: true,
				url: SERVER + v_member.member + v_member.class_enter,
			}).then((res) => res.data);

			if (response.code == Values.SUCCESS_CODE) {
				console.log('response', response);
				setClassVersion(response.class_version);
				const base = member && typeof member === 'object' ? member : ({} as any);
				const next = Object.assign({}, base, {
					id: response.user_id,
					type: 'member',
					companyNum: 0,
					classVersion: response.class_version,
					classCode: classCodeParam,
					language: response.language,
				});
				i18n.changeLanguage(response.language);
				setSavedLanguage(response.language);
				setMember(next);
				return next; // 라우팅은 loginCheck에서 처리
			} else {
				if (response.code === Values.CLASS_FINISH_CODE) {
					enqueueSnackbar(ToastStr.CLASS_ALREADY_END_STR, { variant: 'warning' });
				} else if (response.code == Values.FAIL_CODE) {
					enqueueSnackbar(ToastStr.CLASS_ENTER_FAIL, { variant: 'warning' });
				} else {
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, { variant: 'error' });
				}
			}
			return null;
		} finally {
			creatingRef.current = false;
		}
	};

	const validateMember = async (classCodeParam: string) => {
		let url = new URL(SERVER + v_member.member + v_member.validate);
		let params = { userId: member.id, classCode: classCodeParam } as any;
		url.search = new URLSearchParams(params).toString();

		try {
			const data: any = await axios({ method: 'GET', withCredentials: true, url: url.toString() }).then((res) => res.data);

			if (data.code == Values.SUCCESS_CODE) {
				// 서버 상태대로 최신 member 정보를 가져와 state와 스냅샷을 동기화
				console.log('validateMember data', data);
				const fresh = await getMemberInfo(classCodeParam);
				return fresh;
			} else if (data.code == Values.CLASS_CODE_DIFFERENT_CODE) {
				const m = JSON.parse(JSON.stringify(member));
				const next = Object.assign(m, {
					type: 'member',
					classCode: classCodeParam,
					classVersion: data.class_version,
					language: data.language,
					name: data.member_name,
					companyNum: null,
					companyType: null,
				});
				i18n.changeLanguage(data.language);
				setSavedLanguage(data.language);
				setMember(next);
				return next;
			} else if (data.code == Values.FAIL_CODE) {
				return null;
			} else {
				return null;
			}
		} catch (e) {
			console.error('validateMember error', e);
			return member;
		}
	};

	const getMemberInfo = async (classCodeParam: string) => {
		try {
			const response = await axios.get(SERVER + v_member.member + v_member.info, {
				params: { userId: member.id },
				withCredentials: true,
			});
			const data = response.data;
			if (data.code === Values.SUCCESS_CODE) {
				const next = {
					...member,
					type: 'member',
					classCode: classCodeParam,
					classVersion: data.class_version,
					name: data.member_name,
					companyNum: data.company_num,
					companyType: data.company_type,
					language: data.language,
				};
				i18n.changeLanguage(data.language);
				setSavedLanguage(data.language);
				setMember(next);
				return next;
			} else {
				return member;
			}
		} catch (error) {
			console.error('getMemberInfo error', error);
			return member;
		}
	};

	useEffect(() => {
		//QR 자동 로그인 프로세스
		if (!codeFromUrl || codeFromUrl.trim().length === 0) return;
		loginCheck(codeFromUrl);
	}, [codeFromUrl]);

	return (
		<>
			<div className={classes.paper}>
				<div className={classes.title}>
					<img src="/images/logo.webp" />
				</div>
				<div className={classes.form}>
					<TextField
						variant="standard"
						margin="normal"
						required
						fullWidth
						id="classCode"
						label={label}
						name="classCode"
						autoComplete="off"
						autoFocus
						onFocus={handleFocus}
						onBlur={handleBlur}
						inputProps={{ maxLength: 12, 'data-testid': 'classcode-input', classes: { input: classes.input } }}
						InputLabelProps={{
							classes: { root: classes.inputLabel },
						}}
						value={classCode}
						onChange={valueChangeHandler}
						style={{ color: `${COLORS.CHARCOAL_GREY}` }}
					/>
					<Typography className={classes.desc} style={{ fontFamily: 'NanumR' }}>
						{t('ClassEnter.needClassCode')}
					</Typography>
					<Button fullWidth className={classes.submit} data-testid="enter-class-btn" onClick={() => loginCheck(classCode)}>
						{t('ClassEnter.buttonJoin')}
					</Button>
				</div>
			</div>
			<div className={classes.logoSection}>
				<button {...buttonToOper} className={classes.logobutton} onContextMenu={(e) => e.preventDefault()}>
					<SIcon type="P" />
				</button>
			</div>
		</>
	);
};

export default ClassEnter;
